
import React, { ReactNode, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

interface EnhancedPageTransitionProps {
  children: ReactNode;
  className?: string;
  variant?: 'fade' | 'slide' | 'scale' | 'none';
  direction?: 'up' | 'down' | 'left' | 'right';
  duration?: number;
  delay?: number;
  staggerChildren?: boolean;
}

/**
 * Enhanced Page Transition component for smoother page transitions
 * Uses framer-motion for animations with performance optimizations
 */
const EnhancedPageTransition: React.FC<EnhancedPageTransitionProps> = ({ 
  children, 
  className = '', 
  variant = 'fade',
  direction = 'up',
  duration = 0.5,
  delay = 0,
  staggerChildren = false
}) => {
  const controls = useAnimation();
  
  useEffect(() => {
    // Start animation after a delay to improve perceived performance
    const timer = setTimeout(() => {
      controls.start('animate');
    }, delay * 1000);
    
    return () => clearTimeout(timer);
  }, [controls, delay]);
  
  // Animation variants
  const variants = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 }
    },
    slide: {
      initial: { 
        opacity: 0,
        x: direction === 'left' ? 20 : direction === 'right' ? -20 : 0,
        y: direction === 'up' ? 20 : direction === 'down' ? -20 : 0
      },
      animate: { 
        opacity: 1,
        x: 0,
        y: 0
      },
      exit: { 
        opacity: 0,
        x: direction === 'left' ? -20 : direction === 'right' ? 20 : 0,
        y: direction === 'up' ? -20 : direction === 'down' ? 20 : 0
      }
    },
    scale: {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 1.05 }
    },
    none: {
      initial: {},
      animate: {},
      exit: {}
    }
  };

  // Performance optimizations - use will-change for GPU acceleration
  // but only during animation to prevent excessive memory usage
  const selectedVariant = variants[variant];
  
  // Use will-change hint only when needed to optimize memory usage
  const willChange = variant !== 'none' ? 'opacity, transform' : 'auto';

  return (
    <motion.div
      initial="initial"
      animate={controls}
      exit="exit"
      variants={selectedVariant}
      transition={{ 
        duration,
        // Use more efficient easing function for mobile
        ease: [0.16, 1, 0.3, 1], // Custom spring-like curve with good performance
      }}
      className={className}
      style={{ willChange }}
      // Apply staggered animation to children if requested
      {...(staggerChildren && {
        transition: { 
          staggerChildren: 0.1,
          delayChildren: delay
        }
      })}
    >
      {children}
    </motion.div>
  );
};

export default EnhancedPageTransition;
